import React, { useState } from 'react';
import { Link, Navigate, useNavigate, useSearchParams } from 'react-router-dom';

import AuthPanel from '../components/auth/AuthPanel';
import { useAuth } from '../context/AuthContext';

function RegisterView() {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const { isAuthenticated, register, status } = useAuth();

  const inviteToken = searchParams.get('token')?.trim() || '';

  const [username, setUsername] = useState('');
  const [password, setPassword] = useState('');
  const [errorMessage, setErrorMessage] = useState('');
  const [isSubmitting, setIsSubmitting] = useState(false);

  if (status === 'bootstrapping') {
    return (
      <AuthPanel
        eyebrow="Invite"
        title="Checking your session"
        description="We are making sure you are not already signed in before continuing with registration."
      >
        <p className="auth-panel-meta mb-0">If you already have a session, we will send you back to the app.</p>
      </AuthPanel>
    );
  }

  if (isAuthenticated) {
    return <Navigate replace to="/" />;
  }

  if (!inviteToken) {
    return (
      <AuthPanel
        eyebrow="Invite"
        title="Invalid invite link"
        description="This registration link is missing the invite token needed to create your account."
      >
        <div className="auth-panel-inline-actions auth-panel-stack">
          <Link className="btn auth-panel-secondary" to="/login">
            Return to login
          </Link>
        </div>
        <p className="auth-panel-meta mb-0">
          Ask an administrator to send you a fresh invite link and try again from there.
        </p>
      </AuthPanel>
    );
  }

  const handleSubmit = async (event) => {
    event.preventDefault();
    setErrorMessage('');
    setIsSubmitting(true);

    try {
      await register({
        token: inviteToken,
        username,
        password,
      });

      navigate('/', { replace: true });
    } catch (error) {
      setErrorMessage(error.message || 'Unable to complete registration right now.');
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <AuthPanel
      eyebrow="Invite"
      title="Create your account"
      description="Finish your invite-based registration with a username and password. Your invite determines which email address is activated."
    >
      {errorMessage && (
        <div className="alert alert-danger mt-4 mb-0" role="alert">
          {errorMessage}
        </div>
      )}

      <form className="auth-panel-form" onSubmit={handleSubmit}>
        <div>
          <label className="auth-panel-label" htmlFor="register-username">
            Username
          </label>
          <input
            autoComplete="username"
            className="form-control auth-panel-input"
            id="register-username"
            onChange={(event) => setUsername(event.target.value)}
            required
            type="text"
            value={username}
          />
        </div>

        <div>
          <label className="auth-panel-label" htmlFor="register-password">
            Password
          </label>
          <input
            autoComplete="new-password"
            className="form-control auth-panel-input"
            id="register-password"
            onChange={(event) => setPassword(event.target.value)}
            required
            type="password"
            value={password}
          />
        </div>

        <div className="auth-panel-inline-actions">
          <button
            className="btn btn-dark auth-panel-submit"
            disabled={isSubmitting}
            type="submit"
          >
            {isSubmitting ? 'Creating Account...' : 'Create Account'}
          </button>
        </div>
      </form>
    </AuthPanel>
  );
}

export default RegisterView;
